import React, { FC, memo } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { Grid, Tooltip, Typography} from '@mui/material';
import { useRoutesHelper } from '../../../helpers/routesHelper';
import { useUtils } from '../../../services/useUtils';
import { TicketGroupTEType } from '../book';

const useStyles = makeStyles()((theme) => ({
  link: {
    color: theme.palette.primary.contrastText,
    textDecoration: 'none',
    cursor: 'pointer',
    ':visited': {
      color: theme.palette.primary.contrastText,
    },
  },
  title: {
    fontSize: '0.75rem',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    [theme.breakpoints.down(425)]: {
      fontSize: '0.625rem',
    },
  },
  placeText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    display: 'block',
  },
  placeTextWrapper: {
    minWidth: 0,
    flex: '1 1 0%',
    overflow: 'hidden',
  },
  truncateText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    display: 'block',
  },
  placeContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
    flex: '1 1 auto',
    overflow: 'hidden',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 1,
    minWidth: 0,
    [theme.breakpoints.down(600)]: {
      gap: '8px',
    },
    [theme.breakpoints.down(425)]: {
      gap: '5px',
    },
  },
  quantityTickets: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    [theme.breakpoints.down(600)]: {
      gap: 4,
    },
  },
  price: {
    display: 'flex',
    gap: 11,
    alignItems: 'center',
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    [theme.breakpoints.down(600)]: {
      gap: 4,
    },
  },
  arrowContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    flexBasis: 'auto',
    whiteSpace: 'nowrap',
  },
  arrow: {
    height: '0.75rem',
    width: '10px',
    transition: 'transform 0.3s ease-in-out',
    [theme.breakpoints.down(425)]: {
      height: '0.625rem',
    },
  },
  arrowRotated: {
    transform: 'rotate(90deg)',
  },
  ticketGroup: {
    display: 'flex',
    flexWrap: 'nowrap',
    padding: '10px 12px',
    paddingTop: '10px',
    gap: '10px',
    alignItems: 'center',
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: 'transparent',
    
    '&:not(:first-of-type)::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '12px',
      right: '12px',
      height: '0.5px',
      backgroundColor: '#a8a8a8',
      pointerEvents: 'none',
      [theme.breakpoints.down(600)]: {
        left: '10px',
        right: '10px',
      },
      [theme.breakpoints.down(425)]: {
        left: '8px',
        right: '8px',
      },
    },

    ':hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
    '&:last-child::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '12px',
      right: '12px',
      height: '0.5px',
      backgroundColor: '#a8a8a8',
      pointerEvents: 'none',
      [theme.breakpoints.down(600)]: {
        left: '10px',
        right: '10px',
      },
      [theme.breakpoints.down(425)]: {
        left: '8px',
        right: '8px',
      },
    },
    [theme.breakpoints.down(600)]: {
      gap: '8px',
      padding: '8px 10px',
      paddingTop: '8px',
    },
    [theme.breakpoints.down(425)]: {
      gap: '5px',
      padding: '6px 8px',
      paddingTop: '6px',
    },
  },
  ticketGroupSelected: {
    backgroundColor: theme.palette.secondary.dark,
    ':hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  image: {
    height: '20px',
    width: '20px',
    flexShrink: 0,
    [theme.breakpoints.down(425)]: {
      height: '15px',
      width: '15px',
    },
  },
}));

export const TicketGroup: FC<TicketGroupPropsType> = memo(({ eventSlug, ticketGroup, currency, onTicketGroupClick, isSelected }) => {
  const { classes } = useStyles();
  const { getTicketGroupRoute } = useRoutesHelper();
  const { getEmojiFlagByCurrency } = useUtils();

  if (!ticketGroup) {
    return null;
  }

  const { row, format, retail_price, tevo_section_name, id, available_quantity, in_hand, in_hand_on, public_notes, instant_delivery } = ticketGroup;

  const IC_PURSE_PATH = 'content/img/icPurse.svg';
  const IC_TICKET_PATH = 'content/img/icTicket.svg';
  const IC_ARROW_PATH = 'content/img/arrow.svg';

  const place = `section ${tevo_section_name}${row ? `, row ${row}` : ''}`;

  const costs = currency && `${getEmojiFlagByCurrency(currency)}${retail_price} each`;
  const isEticket = format === 'Eticket';
  const isTmMobile = format === 'TM_modile';
  const inHandDate = `${format}, in hand date: ${in_hand_on}. ${public_notes}`;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onTicketGroupClick) {
      onTicketGroupClick(ticketGroup);
    }
  };

  const ticketGroupContent = (
    <>
      <div className={classes.placeContainer}>
        <span className={classes.placeTextWrapper}>
          <Typography className={`${classes.title} ${classes.placeText}`} component={'span'} title={place}>
            {place}
          </Typography>
        </span>
        <Tooltip title={in_hand ? `${format}, already in hand. ${public_notes}` : inHandDate}>
          <span style={{ flexShrink: 0 }}>{isEticket ? '💻' : '📱'}</span>
        </Tooltip>
        <Tooltip title={in_hand ? `${format}, already in hand. ${public_notes}` : inHandDate}>
          <span style={{ flexShrink: 0 }}>{in_hand ? '✋' : ''}</span>
        </Tooltip>
        <Tooltip title={in_hand ? `${format}, already in hand. ${public_notes}` : inHandDate}>
          <span style={{ flexShrink: 0 }}>{instant_delivery ? '⚡' : ''}</span>
        </Tooltip>
      </div>
      <div className={classes.rightSection}>
        <div className={classes.quantityTickets}>
          <img className={classes.image} src={IC_TICKET_PATH} alt="icTicket" />
          <Typography className={`${classes.title} ${classes.truncateText}`} component={'span'} title={`${available_quantity} tickets`}>{`${available_quantity} tickets`}</Typography>
        </div>
        <div className={classes.price}>
          <img className={classes.image} src={IC_PURSE_PATH} alt={'wallet'} />
          <Typography className={`${classes.title} ${classes.truncateText}`} component={'span'} title={costs}>
            {costs}
          </Typography>
        </div>
        <div className={classes.arrowContainer}>
          <img className={`${classes.arrow} ${isSelected ? classes.arrowRotated : ''}`} src={IC_ARROW_PATH} alt={'arrow'} />
        </div>
      </div>
    </>
  );

  const ticketGroupClassName = `${classes.ticketGroup} ${isSelected ? classes.ticketGroupSelected : ''}`;

  if (onTicketGroupClick) {
    return (
      <div className={classes.link}>
        <Grid item component={'div'} xs={12} className={ticketGroupClassName} onClick={handleClick}>
          {ticketGroupContent}
        </Grid>
      </div>
    );
  }

  return (
    <Link className={classes.link} to={getTicketGroupRoute(eventSlug, id)}>
      <Grid item component={'div'} xs={12} className={ticketGroupClassName}>
        {ticketGroupContent}
      </Grid>
    </Link>
  );
});

type TicketGroupPropsType = {
  ticketGroup: TicketGroupTEType;
  eventSlug: string;
  currency: string;
  onTicketGroupClick?: (ticketGroup: TicketGroupTEType) => void;
  isSelected?: boolean;
};
